import { NavLink, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Button from '../reusable/Button/Button';
import Skeleton from '../reusable/Skeleton/Skeleton';
import InfoTip from '../reusable/InfoTip/InfoTip';
import NotificationGlyph from '../reusable/NotificationGlyph/NotificationGlyph';
import { useTopbarSlotNode } from './TopbarSlot';
import ShareDialog from './ShareDialog';
import AccountSheet from './AccountSheet';
import LanguageSwitcher from './LanguageSwitcher';
import { deauthenticate } from '../store/api/apiSlice';
import {
  useGetAccountsQuery,
  useGetDashboardStatsQuery,
} from '../store/api/dashboardApi';
import {
  toNotification,
  useGetNotificationsQuery,
  useMarkAllNotificationsReadMutation,
  useMarkNotificationReadMutation,
} from '../store/api/notificationsApi';
import { selectAccount, selectPickedAccount } from '../store/slices/accountSlice';
import { selectUser } from '../store/slices/authSlice';
import { PROFILE } from '../pages/Profile/data';
import { accountStateLabel, formatAccountSize, formatLoginId } from '../utilities/accounts';
import { displayNameOf, initialOf } from '../utilities/user';
import { useGetProfileDetailsQuery } from '../store/api/profileApi';
import { NA, usd } from '../utilities/format';
import { debounce } from '../utilities/debounce';
import { GROUPS } from './nav';
import { byGates, useNavGates } from './useNavGates';
import AccountPickerList from './AccountPickerList';
import { t } from '../i18n/i18n';

const icon = (name) => ({ '--icon': `url(/icons/${name}.svg)` });

/** The account picker's search box settles before it asks the API. */
const SEARCH_DELAY = 300;

/**
 * Every page the search box can jump to, off the same list the sidebar renders
 * — a route added to `nav.js` is searchable without a second list to remember.
 * A group with a sub-nav contributes its children instead of itself, so
 * "Certificates" is findable by its own name and not only under "Payout".
 */
const PAGES = GROUPS.flatMap((group) => group.items).flatMap((item) =>
  (item.items ?? [item]).map((page) => ({ ...page, icon: page.icon ?? item.icon })),
);

/**
 * Routes whose own content is keyed to the chosen account.
 *
 * Switching accounts from one of these re-points the page you are on instead of
 * sending you to Account Metrics — the change is already visible where you are
 * standing, so the trip there and back is pure detour. Everywhere else there is
 * nothing on screen for the new account to change, so Account Metrics remains
 * the destination: it is the answer to "show me this account".
 *
 * Membership is not a judgement call — a route belongs here exactly when it
 * resolves an account of its own. Those three do it with the same line:
 *   params.get('login') ?? pickedAccount.loginId ?? firstAccount?.login_id
 * Add a route here when, and only when, it starts doing the same.
 *
 * Deliberately absent: the Request Payout form picks its account in the form
 * body rather than from the top bar; and Breach Analysis, which reads its own
 * account like the others but is a drill-down opened from one account's
 * Account Metrics ("View analysis") — switching account there goes back to
 * Account Metrics for the new one, on request.
 */
const ACCOUNT_SCOPED = [
  '/account-metrics',
  '/account-analysis',
  '/trade-history',
  '/account-setup',
];

const isAccountScoped = (pathname) =>
  ACCOUNT_SCOPED.some((base) => pathname === base || pathname.startsWith(`${base}/`));

/**
 * How an account's state reads in the picker. Three tones rather than one class
 * per value: `progress` is the backend's own vocabulary (In Progress, Time
 * Expired, Failed, Success) and it will grow — a tone keeps a new value
 * readable instead of unstyled.
 */
const statusTone = (account) => {
  if (account.progress === 'Failed') return 'down';
  if (account.progress === 'Success' || account.progress === 'In Progress') return 'up';
  return 'muted';
};

/** The account's `progress` as the API sends it — as the Dashboard table shows it. */
const statusLabel = (account) => account.progress;

/**
 * What the bell holds. Five, not a page: the dropdown is the "what just
 * happened" glance, and the rest of the feed lives at /notifications — which
 * is also what keeps the panel from needing to scroll.
 */
const NOTIFICATION_PAGE_SIZE = 5;

/** Helper to extract numeric amount from API stat fields (object or primitive). */
const resolveAmount = (val) => {
  if (val == null) return null;
  if (typeof val === 'object') {
    return val.amount ?? val.value ?? null;
  }
  return val;
};

export default function Topbar() {
  const slot = useTopbarSlotNode();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isBalanceOpen, setIsBalanceOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isShareOpen, setIsShareOpen] = useState(false);
  const [isAccountSheetOpen, setIsAccountSheetOpen] = useState(false);
  const [query, setQuery] = useState('');
  const gates = useNavGates();

  const term = query.trim().toLowerCase();
  // A page's `keywords` (nav.js) let it answer to what it contains, not only
  // to its name — "calendar" finds Trademate. Each word is matched in English
  // and in the reader's language, so either finds the page.
  const matches = PAGES.filter(byGates(gates)).filter((page) =>
    [page.label, ...(page.keywords ?? [])]
      .flatMap((word) => [word, t(word)])
      .some((word) => word.toLowerCase().includes(term)),
  );

  const closeSearch = () => {
    setIsSearchOpen(false);
    setQuery('');
  };

  /* No keyboard shortcut for the search field (N118): the design prints ⌘F
     there, but claiming Ctrl/⌘+F takes find-in-page away from every page of
     the app, and traders use it to find a ticket or a symbol in a long table.
     The field is reached by clicking it, and the hint is gone with the keys. */

  /** The Dashboard's two queries — whichever mounts first fills the cache. */
  const { data: accountList, isLoading: isAccountsLoading } = useGetAccountsQuery({
    page: 1,
    page_size: 100,
  });
  const { data: summary, isLoading: isSummaryLoading } = useGetDashboardStatsQuery();
  const accounts = accountList?.accounts?.results ?? [];

  /* The bell's own list. Whether the dot shows is read off the cache rather
     than a local flag: marking a row read invalidates the tag, so the dot
     clears because the server says it's read, not because the panel was
     opened once. */
  const { data: notificationPage, isLoading: isNotificationsLoading } =
    useGetNotificationsQuery({ page: 1, page_size: NOTIFICATION_PAGE_SIZE });
  const notifications = (notificationPage?.results ?? []).map(toNotification);
  const hasUnread = notifications.some((note) => !note.read);
  const [markNotificationRead] = useMarkNotificationReadMutation();
  const [markAllNotificationsRead] = useMarkAllNotificationsReadMutation();

  /**
   * The picker's search. A second query rather than a new argument to the one
   * above: that list is also what resolves the selected account and the share
   * sheet's record, and neither should change because of what's typed here.
   */
  const accountSearchRef = useRef(null);
  const [accountTerm, setAccountTerm] = useState('');
  const [accountSearch, setAccountSearch] = useState('');
  const applyAccountSearch = useMemo(
    () => debounce((value) => setAccountSearch(value.trim()), SEARCH_DELAY),
    [],
  );

  const clearAccountSearch = () => {
    setAccountTerm('');
    setAccountSearch('');
    applyAccountSearch('');
  };

  const closeAccountDropdown = () => {
    setIsDropdownOpen(false);
    clearAccountSearch();
  };

  const topbarRef = useRef(null);
  const actionsRef = useRef(null);
  const [isActionsWrapped, setIsActionsWrapped] = useState(false);

  useLayoutEffect(() => {
    const checkWrap = () => {
      const topbar = topbarRef.current;
      const actions = actionsRef.current;
      if (!topbar || !actions) return;

      const searchEl = topbar.querySelector('.topbar__search');
      if (!searchEl) return;

      const isWrapped = actions.offsetTop > searchEl.offsetTop + 10;
      setIsActionsWrapped((prev) => (prev !== isWrapped ? isWrapped : prev));
    };

    checkWrap();

    const observer = new ResizeObserver(checkWrap);
    if (topbarRef.current) observer.observe(topbarRef.current);
    if (actionsRef.current) observer.observe(actionsRef.current);
    window.addEventListener('resize', checkWrap);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', checkWrap);
    };
  }, []);


  /** The funded total, not `trader_badge.balance` — that's one account's. */
  const balance = resolveAmount(summary?.funded_balance ?? summary?.fundedBalance);
  const availableBalance = resolveAmount(
    summary?.available_balance,
  );
  const pendingPayout = resolveAmount(
    summary?.pending_payout
  );
  const lifetimeProfit = resolveAmount(
    summary?.lifetime_profit
  );
  const lifetimePayout = resolveAmount(
    summary?.lifetime_payouts
  );

  /**
   * The breakdown, written once: the balance pill's dropdown draws it on a
   * desktop and the account sheet draws it on a phone, and two copies would be
   * two places for them to disagree. `info` is what each figure means, in the
   * backend's words — the `description` `/v2/dashboard/` sends beside it —
   * shown behind an (i); a figure without one simply has no (i).
   */
  const balances = [
    {
      label: 'Qualified Balance',
      info: summary?.funded_balance?.description,
      value: isSummaryLoading ? <Skeleton w="56px" h={11} /> : usd(balance),
    },
    {
      label: 'Available Balance',
      info: summary?.available_balance?.description,
      value: isSummaryLoading
        ? <Skeleton w="56px" h={11} />
        : usd(availableBalance, { fallback: '---' }),
    },
    {
      label: 'Pending Payout',
      info: summary?.pending_payout?.description,
      value: isSummaryLoading
        ? <Skeleton w="56px" h={11} />
        : usd(pendingPayout, { fallback: '---' }),
    },
    {
      label: 'Lifetime Profit',
      info: summary?.lifetime_profit?.description,
      value: isSummaryLoading
        ? <Skeleton w="56px" h={11} />
        : usd(lifetimeProfit, { fallback: '---' }),
      rule: true,
    },
    {
      label: 'Lifetime Payout',
      info: summary?.lifetime_payouts?.description,
      value: isSummaryLoading
        ? <Skeleton w="56px" h={11} />
        : usd(lifetimePayout, { fallback: '---' }),
    },
  ];

  /** The profile's name, as in Sidebar. */
  const user = useSelector(selectUser);
  const { data: profile } = useGetProfileDetailsQuery(undefined, { skip: !user });
  const traderName = displayNameOf(user, PROFILE.firstName, profile);
  const traderEmail = user?.email ?? PROFILE.email;

  /**
   * Same precedence the account-scoped pages use: the URL wins, then the
   * picker's own choice, then the first account on the profile.
   */
  const [params] = useSearchParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const picked = useSelector(selectPickedAccount);
  const candidateId = params.get('login') ?? picked.loginId;
  const matchedAccount = accounts.find(
    (a) => String(a.login_id) === String(candidateId)
  );
  // An unknown login falls back to the first account — but only once the
  // whole list is in hand: it is the first hundred, and an account picked
  // from the picker's later pages (N22) isn't unknown, just not loaded here.
  const listIsWhole =
    accountList != null && (accountList.accounts?.count ?? accounts.length) <= accounts.length;
  // Never a login the list can't vouch for once it is whole: a trader with no
  // accounts showed — and shared — whatever the address or the last session named.
  const selectedAccountId = matchedAccount
    ? matchedAccount.login_id
    : candidateId && accountList != null && !listIsWhole
      ? candidateId
      : accounts[0]?.login_id ?? null;

  const handleAccountSelect = (account) => {
    dispatch(selectAccount({ loginId: account.login_id, platform: account.platform ?? null }));
    closeAccountDropdown();

    const stay = isAccountScoped(pathname);

    /* The query has to be rewritten even when we stay put. The account-scoped
       pages read `?login=` *ahead* of the picker's Redux state — the URL is
       their first source of truth — so dispatching alone would leave the old id
       in the address bar still winning, and the page would ignore the switch.

       Staying: keep whatever else the page put in the query and change only the
       account. Leaving: start clean, since another page's parameters mean
       nothing to Account Metrics. */
    const query = new URLSearchParams(stay ? params : undefined);
    query.set('login', account.login_id);
    if (account.platform) query.set('platform', account.platform);
    else query.delete('platform');

    navigate(`${stay ? pathname : '/account-metrics'}?${query}`, {
      /* Re-pointing the page you are already on is a change of subject, not a
         step in the journey. Pushing would turn Back into an account-undo and
         bury the page you actually arrived from under one entry per switch. */
      replace: stay,
    });
  };

  return (
    <header className="topbar" ref={topbarRef}>
      {/* The sidebar carries the wordmark, and the sidebar is gone on a phone.
          The mark alone stands in for it there — no room for the full logo, and
          nothing else in the row identifies the app. */}
      <NavLink to="/dashboard" className="topbar__brand" aria-label={t('Go to dashboard')}>
        <span className="icon" style={icon('brand-mark')} />
      </NavLink>

      {/* Click-outside Backdrop */}
      {(isDropdownOpen || isNotificationsOpen || isBalanceOpen || isSearchOpen) && (
        <div
          className="topbar__dropdown-backdrop"
          onClick={() => {
            closeAccountDropdown();
            setIsNotificationsOpen(false);
            setIsBalanceOpen(false);
            closeSearch();
          }}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 99,
            background: 'transparent',
          }}
        />
      )}

      <div className={`topbar__search ${isSearchOpen ? 'is-open' : ''}`}>
        <span className="icon" style={icon('topbar-search')} />
        <input
          type="search"
          placeholder={t('Search...')}
          aria-label={t('Search pages')}
          value={query}
          onFocus={() => setIsSearchOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsSearchOpen(true);
          }}
          onKeyDown={(e) => e.key === 'Escape' && closeSearch()}
        />

        <div className={`topbar__search-dropdown ${isSearchOpen ? 'is-open' : ''}`}>
          {matches.map((page) => (
            <NavLink
              key={page.to}
              to={page.to}
              end
              className={({ isActive }) =>
                `topbar__search-item ${isActive ? 'is-active' : ''}`
              }
              onClick={closeSearch}
            >
              <span className="icon" style={icon(page.icon)} />
              {t(page.label)}
            </NavLink>
          ))}

          {matches.length === 0 && (
            <div className="topbar__search-item is-empty">
              {t('No pages match “{{query}}”', { query: query.trim() })}
            </div>
          )}
        </div>
      </div>

      <div className="topbar__balance-wrapper">
        <button
          type="button"
          className={`topbar__pill topbar__pill--balance ${isBalanceOpen ? 'is-active' : ''}`}
          onClick={() => setIsBalanceOpen(!isBalanceOpen)}
        >
          <span className="icon" style={icon('topbar-wallet')} />
          <span className="topbar__muted">{t('Qualified Balance')}</span>
          {/* A bar, not `N/A`: the shell mounts before the response, and N/A
              reads as "this profile has no funded balance". */}
          {isSummaryLoading ? <Skeleton w="64px" h={11} /> : <span>{usd(balance)}</span>}
        </button>

        <div className={`topbar__balance-dropdown ${isBalanceOpen ? 'is-open' : ''}`}>
          {balances.map((row) => (
            <React.Fragment key={row.label}>
              {row.rule && <div className="topbar__balance-divider" />}
              <div className="topbar__balance-row">
                <span className="topbar__balance-label">
                  {t(row.label)}
                  {row.info && (
                    <InfoTip
                      className="topbar__balance-info"
                      label={t('What {{label}} means', { label: t(row.label) })}
                      title={t(row.label)}
                      glyph="i"
                    >
                      {row.info}
                    </InfoTip>
                  )}
                </span>
                <span className="topbar__balance-value">{row.value}</span>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Account Setup is where the credentials actually live. It resolves the
          account itself — from the picker's own state when the URL carries
          none — so this navigates plainly and lets that page put `?login=` in
          the address bar, rather than duplicating the precedence here. */}
      <Button
        className="topbar__pill--credentials"
        onClick={() => navigate('/account-setup')}
      >
        {t('Credentials')}
      </Button>

      <div className="topbar__spacer" />

      {/* page-provided controls — sits on the right, just left to Public Page */}
      {slot && <div className="topbar__slot">{slot}</div>}

      <Button className="topbar__pill--public" onClick={() => setIsShareOpen(true)}>
        {t('Public Page')}
      </Button>

      <ShareDialog
        open={isShareOpen}
        accountId={selectedAccountId}
        account={accounts.find((a) => a.login_id === selectedAccountId)}
        onClose={() => setIsShareOpen(false)}
      />

      <div
        className={`topbar__actions${isActionsWrapped ? ' is-wrapped' : ''}`}
        ref={actionsRef}
      >
        {/* N78 — the language selector, on the app's own translations (src/i18n). */}
        <LanguageSwitcher />

        <div className="topbar__bell-wrapper">
          <button
            type="button"
            className={`topbar__bell ${isNotificationsOpen ? 'is-active' : ''}`}
            aria-label={t('Notifications')}
            onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
          >
            <span className="icon" style={icon('topbar-bell')} />
            {hasUnread && <span className="topbar__dot" />}
          </button>

          <div className={`topbar__notifications-dropdown ${isNotificationsOpen ? 'is-open' : ''}`}>
            <div className="topbar__notifications-header">
              <span className="topbar__notifications-title">{t('Notifications')}</span>
              <button
                type="button"
                className="topbar__notifications-mark-read"
                disabled={!hasUnread}
                onClick={() => markAllNotificationsRead()}
              >
                {t('Mark all read')}
              </button>
            </div>

            <div className="topbar__notifications-divider" />

            <div className="topbar__notifications-list">
              {notifications.map((n) => (
                <button
                  type="button"
                  key={n.id}
                  className={`topbar__notification-item${n.read ? '' : ' is-unread'}`}
                  onClick={() => !n.read && markNotificationRead(n.id)}
                >
                  <div className={`topbar__notification-icon-wrapper topbar__notification-icon-wrapper--${n.type}`}>
                    <NotificationGlyph tone={n.type} />
                  </div>
                  <div className="topbar__notification-content">
                    <span className="topbar__notification-item-title">{n.title}</span>
                    <span className="topbar__notification-item-message">{n.message}</span>
                    <span className="topbar__notification-item-time">{n.time}</span>
                  </div>
                </button>
              ))}

              {!isNotificationsLoading && notifications.length === 0 && (
                <p className="topbar__notifications-empty">{t('Nothing new right now.')}</p>
              )}
            </div>

            <NavLink
              to="/notifications"
              className="topbar__notifications-all"
              onClick={() => setIsNotificationsOpen(false)}
            >
              {t('View all notifications')}
            </NavLink>
          </div>
        </div>

        <div className="topbar__account-wrapper">
          <button
            type="button"
            className={`topbar__account ${isDropdownOpen ? 'is-active' : ''}`}
            onClick={() => {
              if (isDropdownOpen) {
                closeAccountDropdown();
                return;
              }
              setIsDropdownOpen(true);
              // The panel is only hidden while closed, so the field is already there.
              accountSearchRef.current?.focus({ preventScroll: true });
            }}
          >
            <span className="topbar__account-badge">A</span>
            {isAccountsLoading ? (
              <Skeleton w="62px" h={11} className="topbar__account-id" />
            ) : (
              <span className="topbar__account-id">
                {selectedAccountId ? formatLoginId(selectedAccountId) : NA}
              </span>
            )}
            <span className="icon" style={icon('topbar-chevron')} />
          </button>

          <div className={`topbar__account-dropdown ${isDropdownOpen ? 'is-open' : ''}`}>
            <label className="topbar__account-search">
              <span className="icon" style={icon('table-search')} />
              <input
                ref={accountSearchRef}
                type="search"
                placeholder={t('Search accounts')}
                aria-label={t('Search accounts')}
                value={accountTerm}
                // Hidden, not unmounted, while closed — keep it out of the tab order.
                tabIndex={isDropdownOpen ? 0 : -1}
                onChange={(e) => {
                  setAccountTerm(e.target.value);
                  applyAccountSearch(e.target.value);
                }}
                onKeyDown={(e) => e.key === 'Escape' && closeAccountDropdown()}
              />
            </label>

            {/* Every account, a hundred at a time as the list scrolls (N22). */}
            <AccountPickerList
              search={accountSearch}
              renderRow={(acc) => {
                const isSelected = String(acc.login_id) === String(selectedAccountId);
                return (
                  <div
                    key={acc.id ?? acc.login_id}
                    className={`topbar__account-item ${isSelected ? 'is-selected' : ''}`}
                    onClick={() => handleAccountSelect(acc)}
                  >
                    <div className="topbar__account-item-left">
                      <span className="topbar__account-item-id">{formatLoginId(acc.login_id)}</span>
                      <span className="topbar__account-item-meta">
                        {formatAccountSize(acc.account_size)} · {t(accountStateLabel(acc.status))} ·{' '}
                        <span
                          className={`topbar__account-item-status topbar__account-item-status--${statusTone(
                            acc,
                          )}`}
                        >
                          {t(statusLabel(acc))}
                        </span>
                      </span>
                    </div>
                    {isSelected && (
                      <span className="icon topbar__account-item-check" style={icon('check-sm')} />
                    )}
                  </div>
                );
              }}
            />
          </div>
        </div>
      </div>

      {/* The phone's own account menu. Hidden above the `mobile` rule, where
          the sidebar's account card already carries these links. */}
      <button
        type="button"
        className={`topbar__avatar${isAccountSheetOpen ? ' is-active' : ''}`}
        aria-haspopup="menu"
        aria-expanded={isAccountSheetOpen}
        aria-label={t('Account menu')}
        onClick={() => {
          // One panel at a time: the others are anchored to the same row.
          closeAccountDropdown();
          setIsBalanceOpen(false);
          setIsNotificationsOpen(false);
          closeSearch();
          setIsAccountSheetOpen((open) => !open);
        }}
      >
        {initialOf(traderName)}
      </button>

      <AccountSheet
        open={isAccountSheetOpen}
        onClose={() => setIsAccountSheetOpen(false)}
        name={traderName}
        email={traderEmail}
        balances={balances}
        notifications={notifications}
        hasUnread={hasUnread}
        onMarkRead={() => markAllNotificationsRead()}
        onPublicPage={() => {
          setIsAccountSheetOpen(false);
          setIsShareOpen(true);
        }}
        onSignOut={() => {
          setIsAccountSheetOpen(false);
          dispatch(deauthenticate());
        }}
      />
    </header>
  );
}
